import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Tabs, Tab, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import AuthLayout from '../components/auth/AuthLayout';
import { loginWithPasswordApi, requestOtpApi, loginWithOtpApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './LoginPage.css';

const LoginPage = () => {
    const navigate = useNavigate();
    const { login } = useAuth();

    // Form States
    const [loginMethod, setLoginMethod] = useState('password');
    const [identifier, setIdentifier] = useState(''); // Email or Mobile
    const [password, setPassword] = useState('');
    const [otp, setOtp] = useState('');

    // UI States
    const [otpSent, setOtpSent] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' }); // Combined error/info state
    const [loading, setLoading] = useState(false);

    // Reset messages when switching tabs
    const handleTabSelect = (k) => {
        setLoginMethod(k);
        setOtpSent(false);
        setMessage({ type: '', text: '' });
    };

    const handleSuccessfulLogin = (apiResponseData) => {
        login(apiResponseData.token, {
            id: apiResponseData.id,
            fullName: apiResponseData.fullName,
            email: apiResponseData.email,
            role: apiResponseData.role,
            roleCode: apiResponseData.roleCode,
            joget_user_id: apiResponseData.joget_user_id
        });
        navigate('/dashboard');
    };

    const handleError = (err) => {
        const errorMsg = err.response?.data?.message || 'Something went wrong. Please try again.';
        setMessage({ type: 'danger', text: errorMsg });
    };

    const handlePasswordLogin = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });
        setLoading(true);
        try {
            const { data } = await loginWithPasswordApi({ email: identifier, password });
            handleSuccessfulLogin(data);
        } catch (err) {
            handleError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleRequestOtp = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });
        setLoading(true);
        try {
            const { data } = await requestOtpApi({ identifier });
            setOtpSent(true);
            setMessage({ type: 'success', text: data.message || 'OTP sent successfully!' });
        } catch (err) {
            handleError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleOtpLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await loginWithOtpApi({ identifier, otp });
            handleSuccessfulLogin(data);
        } catch (err) {
            setMessage({ type: 'danger', text: 'Invalid or expired OTP.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout
            pageTitle="Login"
            sidebarTitle="Welcome Back"
            subtitle="Sign in to your account to continue"
             className="login-auth-layout" 
        >
            <Tabs
                activeKey={loginMethod}
                onSelect={handleTabSelect}
                className="mb-4 custom-login-tabs"
                justify
            >
                {/* --- Password Login Tab --- */}
                <Tab eventKey="password" title="Password">
                    {message.text && message.type === 'danger' && (
                        <Alert variant="danger" className="py-2 small">{message.text}</Alert>
                    )}
                    <Form onSubmit={handlePasswordLogin}>
                        <Form.Group className="mb-3">
                            <Form.Label className="form-label-custom">Email or Mobile</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter Email or Mobile"
                                value={identifier}
                                onChange={(e) => setIdentifier(e.target.value)}
                                required
                                className="form-ctrl-login-custom"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="form-label-custom">Password</Form.Label>
                            <Form.Control
                                type="password"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="form-ctrl-login-custom"
                            />
                        </Form.Group>

                        <div className="remember-forgot-wrapper mb-4">
                            <Form.Check
                                type="checkbox"
                                label={<span className="text-white small">Remember me</span>}
                            />
                            <Link to="/forgot-password" size="sm" className="text-pink-theme small font">
                                Forgot password?
                            </Link>
                        </div>

                        <Button type="submit" className="btn-auth-primary w-100" disabled={loading}>
                            {loading ? <Spinner size="sm" animation="border" /> : 'Sign In'}
                        </Button>
                    </Form>
                </Tab>

                {/* --- OTP Login Tab --- */}
                <Tab eventKey="otp" title="Login with OTP">
                    {message.text && (
                        <Alert variant={message.type === 'danger' ? 'danger' : 'info'} className="py-2 small">
                            {message.text}
                        </Alert>
                    )}

                    {!otpSent ? (
                        <Form onSubmit={handleRequestOtp}>
                            <Form.Group className="mb-4">
                                <Form.Label className="form-label-custom">Email or Mobile</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter registered Email or Mobile"
                                    value={identifier}
                                    onChange={(e) => setIdentifier(e.target.value)}
                                    required
                                    className="form-ctrl-login-custom"
                                />
                            </Form.Group>
                            <Button type="submit" className="btn-auth-primary w-100" disabled={loading}>
                                {loading ? <Spinner size="sm" animation="border" /> : 'Send OTP'}
                            </Button>
                        </Form>
                    ) : (
                        <Form onSubmit={handleOtpLogin}>
                            <Form.Group className="mb-4">
                                <Form.Label className="form-label-custom text-center d-block">
                                    Enter 6-digit code sent to <br /> <strong>{identifier}</strong>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="000000"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    className="form-ctrl-login-custom text-center otp-input"
                                    style={{ letterSpacing: '0.4em', fontSize: '24px', fontWeight: 'bold' }}
                                    required
                                    autoFocus
                                />
                            </Form.Group>
                            <Button type="submit" className="btn-auth-primary w-100" disabled={loading || otp.length < 4}>
                                {loading ? <Spinner size="sm" animation="border" /> : 'Verify & Login'}
                            </Button>
                            <div className="text-center mt-3">
                                <Button 
                                    variant="link" 
                                    size="sm" 
                                    onClick={() => setOtpSent(false)} 
                                    className="text-muted small text-decoration-none"
                                >
                                    Change Email/Mobile
                                </Button>
                            </div>
                        </Form>
                    )}
                </Tab>
            </Tabs>

            <div className="text-center mt-4">
                <p className="small text-white">
                    Don't have an account? <Link to="/register" className="text-pink-theme link">Sign up</Link>
                </p>
            </div>
        </AuthLayout>
    );
};

export default LoginPage;